/**
 * Eigene Platzhalter im Wizard — gleiche Speicherung wie im alten Builder (`offer.customPlaceholders`: Name → Wert),
 * ohne die beiden, die der Wizard selbst führt (`meetingLink` = Kalenderlink, `referralBonus` = Prämie). Rein und testbar.
 */

import {
  RESERVED_PLACEHOLDER_KEYS,
  WizardAutoReply,
  WizardCustomPlaceholder,
} from '../../campaign-wizard.types';

/** Vorschläge für den Schlüssel (Kundenwunsch); freie Eingabe bleibt möglich. */
export const PLACEHOLDER_KEY_SUGGESTIONS = ['Benefits', 'Gehalt', 'Home Office', 'Extragelder'] as const;

/** Schlüssel wie im Backend: getrimmt, Leerraum zu einem Leerzeichen zusammengezogen („Home  Office “ → „Home Office“). */
export const normalizeKey = (key: string): string => key.replace(/\s+/g, ' ').trim();

/** Buchstaben (auch Umlaute), Ziffern und `_`, Wörter durch Leerzeichen getrennt — nur das findet `{{Schlüssel}}` zur Laufzeit wieder. */
export const isValidKey = (key: string): boolean => /^[\p{L}_][\p{L}\p{N}_]*(?: [\p{L}\p{N}_]+)*$/u.test(normalizeKey(key));

/** Vorschläge, die noch frei sind: schon vergebene Schlüssel (ohne Beachtung der Groß-/Kleinschreibung) fallen weg; `ownKey` bleibt beim Bearbeiten wählbar. */
export const freeKeySuggestions = (list: WizardCustomPlaceholder[], ownKey?: string): string[] => {
  const used = new Set(list.filter(p => p.key !== ownKey).map(p => p.key.toLowerCase()));
  return PLACEHOLDER_KEY_SUGGESTIONS.filter(s => !used.has(s.toLowerCase()));
};

/** Zur Laufzeit wird ohne Beachtung der Groß-/Kleinschreibung ersetzt, daher gelten „Gehalt“ und „gehalt“ als derselbe Schlüssel. */
const sameKey = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

const isReserved = (key: string): boolean =>
  (RESERVED_PLACEHOLDER_KEYS as readonly string[]).includes(key);

/** Wie im alten Builder: Name (getrimmt, nur gültige Zeichen) und Wert getrimmt, leer zählt nicht, ein vorhandener Name wird ersetzt. */
export const addPlaceholder = (
  list: WizardCustomPlaceholder[],
  key: string,
  value: string
): WizardCustomPlaceholder[] => {
  const name = normalizeKey(key);
  if (!name || !value.trim() || isReserved(name) || !isValidKey(name)) return list;
  return [...list.filter(p => !sameKey(p.key, name)), { key: name, value: value.trim() }];
};

export const deletePlaceholder = (
  list: WizardCustomPlaceholder[],
  key: string
): WizardCustomPlaceholder[] => list.filter(p => p.key !== key);

export const editPlaceholder = (
  list: WizardCustomPlaceholder[],
  originalKey: string,
  newKey: string,
  newValue: string
): WizardCustomPlaceholder[] => {
  const name = normalizeKey(newKey);
  if (!name || isReserved(name) || !isValidKey(name)) return list;
  // Kollision mit einem ANDEREN Eintrag: Liste unverändert lassen, statt ihn still zu löschen.
  if (list.some(p => p.key !== originalKey && sameKey(p.key, name))) return list;
  return [...list.filter(p => p.key !== originalKey), { key: name, value: newValue.trim() }];
};

/** Kommt `{{meetingLink}}` in einem Fall oder im Ziel der automatischen Antworten vor? */
export const autoReplyUsesMeetingLink = (autoReply: WizardAutoReply | null): boolean =>
  !!autoReply &&
  [autoReply.goalofCommunication, ...autoReply.caseHandling.map(c => c.promptToReply)].some(text =>
    text.includes('{{meetingLink}}')
  );
